import { useCallback, useRef } from 'react';
import { getExcelFileName } from './ExcelUtil';
import { FlexGrid } from '@grapecity/wijmo.grid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import he from 'he';

import dayjs from 'dayjs';

export const CommonUtil = {
  /**
   * 날짜 변환 (Date, string(2025.02.04) -> 20250204)
   * @param data
   * @Author jd
   */
  toYmd(v: Date | string): string | null {
    const tzOffset = 9 * 60 * 60 * 1000;
    const fmtDate = (d: Date) => {
      const local = new Date(d.getTime() + tzOffset);
      const y = local.getUTCFullYear();
      const m = String(local.getUTCMonth() + 1).padStart(2, '0');
      const day = String(local.getUTCDate()).padStart(2, '0');
      return `${y}${m}${day}`;
    };
    if (v instanceof Date && !isNaN(v.getTime())) return fmtDate(v);
    if (typeof v === 'string') {
      const s = v.trim().replace(/[-.]/g, '');
      if (/^\d{8}$/.test(s)) return s;
      const d = new Date(v);
      if (!isNaN(d.getTime())) return fmtDate(d);
    }
    return null;
  },
  /**
   * object -> array 변환
   * @param data
   * @Author jd
   */
  nomalizeToArray(data: any) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') return [data];
    return [];
  },
  /**
   * 소수점 {digits}자리까지 표현 (소수점 {digits + 1}자리에서 반올림처리)
   * @param value
   * @Author jd
   */
  roundNumber(value: number, digits = 1) {
    digits = Number(digits || 1);
    if (isNaN(digits)) {
      digits = 1;
    }
    return Number((Math.round(value * 100) / 100).toFixed(digits)).toLocaleString();
  },

  /**
   * 콤마(, ) 제거
   * @param str
   * @Author jd
   */
  removeComma(str = '') {
    return String(str || '').replace(/,/g, '');
  },
  /**
   * 숫자 타입으로 변환
   * ex. '1,234.0' -> 1234
   * @param str
   * @Author jd
   */
  convertNumber(str = '') {
    return Number(this.removeComma(str));
  },

  /**
   * 소수점 2자리 버림 처리 (값이 없는 경우 0 노출)
   * @param val
   */
  defaultZeroNumberTrunc(val) {
    const value = this.convertNumber(val || 0);
    if (!value || isNaN(value)) return 0;
    return (Math.trunc(value * 100) / 100).toLocaleString();
  },

  /**
   * 숫자 셀 템플릿 (값이 없는 경우 0 노출)
   * @param val
   * @Author jd
   * @constructor
   */
  defaultZeroNumberCellTemplate(val, digits = 1) {
    const value = this.convertNumber(val || 0);
    if (!value || isNaN(value)) return 0;
    return this.roundNumber(value, digits);
  },

  /**
   * 날짜포맷 (ex. 2024.05.05 - 2024.05.05)
   * @param stDt, endDt
   * @Author jd
   * @constructor
   */
  formatPeriod(stDt?: string, endDt?: string) {
    const parse = (dateStr?: string) => {
      if (!dateStr) return null;
      const d = dayjs(dateStr, ['YYYY-MM-DD', 'YYYY.MM.DD', 'YYYYMMDD'], true);
      return d.isValid() ? d : null;
    };

    const s = parse(stDt);
    const e = parse(endDt);

    if (!s && !e) return '';

    const fmt = (d: dayjs.Dayjs) => d.format('YYYY.MM.DD');

    if (s && e) return `${fmt(s)} - ${fmt(e)}`;
    if (s) return fmt(s);
    if (e) return fmt(e);

    return '';
  },
  /**
   * useRefValue
   * @param initialValue
   * @Author jd
   * @constructor
   */
  useRefValue<T>(initialValue: T) {
    const ref = useRef<T>(initialValue);

    /** 전체 객체 반환 */
    const get = useCallback(() => ref.current, []);

    /** 전체 객체 교체 */
    const set = useCallback((value: T) => {
      ref.current = value;
    }, []);

    /** 특정 key의 값 가져오기 */
    const getKeySafe = useCallback(
      <K extends keyof T>(key: K, defaultVal: NonNullable<T[K]>): NonNullable<T[K]> => {
        const cur = ref.current as T | undefined | null;
        if (cur == null) return defaultVal;
        const v = cur[key];
        return (v ?? defaultVal) as NonNullable<T[K]>;
      },
      []
    );

    /** 특정 key의 값 갱신 */
    const setKey = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
      ref.current = { ...ref.current, [key]: value };
    }, []);

    /** 여러 key를 한 번에 갱신 (Patch) */
    const patch = useCallback((partial: Partial<T>) => {
      ref.current = { ...ref.current, ...partial };
    }, []);

    /** 초기화 (원하면 초기값으로 복원 가능) */
    const reset = useCallback(() => {
      ref.current = structuredClone(initialValue);
    }, [initialValue]);

    return { get, set, getKeySafe, setKey, patch, reset };
  },
  /**
   * [ELM 공통 - Wijmo 엑셀 export]
   *
   * 엑셀 다운르드를 처리하는 함수
   * @Author jd
   **/
  exportWijmoExcelGrid(flexRef, fileName, sheetName) {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(
      flexRef,
      {
        sheetName: sheetName,
        includeColumnHeaders: true,
        includeRowHeaders: true,
        formatItem: (args) => {
          const xlsxCell = args.xlsxCell;
          const cell = args.getFormattedCell();
          const text = cell?.textContent?.trim();
          xlsxCell.value = text;
          if (!text) {
            // [25.12.12] textContent 없는 경우 dataItem값 export
            const grid = args?.panel?.grid;
            if (!grid) return;
            if (args.panel === grid?.cells) {
              const col = grid?.columns?.[args.col];
              const item = grid?.rows?.[args.row]?.dataItem;
              if (item && col?.binding) {
                xlsxCell.value = item[col.binding] || '';
              }
            }
          }
        },
      },
      getExcelFileName(fileName),
      (base64) => {
        return;
      },
      (reason) => {
        console.error('error :', reason);
      },
      undefined,
      true,
      50
    );
  },

  /**
   * 시트명 오류 방지
   * @param name
   */
  cleanSheetName(name: string) {
    if (!name) return 'Sheet';
    // 시트명에 '/' 포함 시 오류 -> '_' 대체
    let result = name.replaceAll('/', '_');
    result = result.trim();
    if (result.length > 30) {
      result = result.substring(0, 30);
    }
    if (result.length < 1) result = 'Sheet';
    return result;
  },

  /**
   * 위즈모 엑셀 다운로드 시 시트 생성 (시트 다건 생성해야하는 경우 사용)
   * @param sheetNm
   * @param layoutDefinition
   * @param rowData
   */
  createWijmoSheet(sheetNm: string, layoutDefinition: any[], rowData: any[]) {
    const container = document.createElement('div');
    container.style.display = 'none';
    document.body.appendChild(container);

    const grid = new FlexGrid(container, {
      autoGenerateColumns: false,
      allowMerging: 'ColumnHeaders',
      columnGroups: layoutDefinition,
      itemsSource: rowData,
    });

    const result = wjGridXlsx.FlexGridXlsxConverter.save(grid, {
      includeColumnHeaders: true,
      includeCellStyles: true,
      formatItem: (args) => {
        if (args.panel === grid!.cells) {
          const cell = args.getFormattedCell();
          if (cell) {
            args.xlsxCell.value = cell?.textContent?.trim();
          }
        }
      },
    });

    grid.dispose();
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }

    const sheet = result.sheets[0];
    sheet.name = this.cleanSheetName(sheetNm);
    return sheet;
  },

  /**
   * [ELM 공통 - HTML 엔티티 치환 함수]
   *
   * Html String을 unescapeHtml 처리하는 함수.
   * @Author jd
   **/
  unescapeHtml(htmlString: string) {
    if (!htmlString) return '';

    /*
    const matchMap: { [key: string]: string } = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&#40;': '(',
      '&#41;': ')',
      '&#35;': '#',
      '&#59;': ':',
      '&#34;': '"',
      '&#39;': "'",
      '&#92;': '\\',
      '&#47;': '/',
      '&Phi;': 'Φ',
      '&plusmn;': '±',
      '&quot;': '"',
    };

    let result = htmlString;

    while (prev !== result) {
      prev = result;
      result = result.replace(
        /&amp;|&lt;|&gt;|&#40;|&#41;|&#35;|&#59;|&#34;|&#39;|&#92;|&#47;|&Phi;|&plusmn;/g,
        (match) => matchMap[match] || match
      );
    }
    */

    let prev = '';
    let result = htmlString;
    while (prev != result) {
      prev = result;
      result = he.decode(result);
    }
    return result;
  },

  /**
   * [ELM 공통 - HTML 엔티티 치환 함수]
   *
   * Html String을 escapeHtml 처리하는 함수.
   * @Author jd
   **/
  escapeHtml(htmlString) {
    return htmlString?.replace(/[&<>()#:"'\\/]/g, (match) => {
      switch (match) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '(':
          return '&#40;';
        case ')':
          return '&#41;';
        case '#':
          return '&#35;';
        case ':':
          return '&#59;';
        case '"':
          return '&#34;';
        case "'":
          return '&#39;';
        case '\\':
          return '&#92;';
        case '/':
          return '&#47;';
        default:
          return match;
      }
    });
  },

  /**
   * [ELM 공통 - XSS 공격 방지용 함수]
   *
   * 공지사항 같은 경우 HTML을 그대로 노출시켜야하므로
   * 해당 함수를 사용하여 sanitize 처리 하도록 한다.
   * 그리드에서 사용할 경우 escape, 아닐 경우 unescape처리
   *
   * @Author jd
   **/
  safeHtmlContents(html, isGrid = true) {
    if (!html || typeof html !== 'string') return html;
    let decodeStr = this.unescapeHtml(html) || '';
    decodeStr = decodeStr
      .replace(/<script.*?>.*?<\/script>/g, '')
      .replace(/<style.*?>.*?<\/style>/g, '')
      .replace(/<iframe.*?>.*?<\/iframe>/g, '')
      .replace(/<details.*?>.*?<\/details>/g, '')
      .replace(/<img.*?>.*?<\/img>/g, '')
      .replace(/<object.*?>.*?<\/object>/g, '')
      .replace(/<[^>]*?\s+on\w+=(["']?)[^"'>]*\1[^>]*?>/gi, '');
    decodeStr = decodeStr?.replace(
      /<[^>]*\s(?:on\w+=|javascript:|<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>).*?>/gi,
      ''
    );

    if (isGrid) {
      return this.escapeHtml(decodeStr);
    }

    return this.unescapeHtml(decodeStr);
  },

  /**
   * HTML 엔티티를 일반 문자열로 디코드합니다.
   * ex) &lt;div&gt; → <div>
   */
  decodeContents(contents) {
    let ta: HTMLTextAreaElement | null = null;
    if (!contents) return '';
    if (!ta) ta = document.createElement('textarea');
    ta.innerHTML = contents;
    return ta.value;
  },
};
